import React, { memo } from 'react'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import {
  StaticTextProvider,
  useTranslatedText,
  InternationalStrings,
} from '../../../../StaticText/StaticTextContext'
import { H4 } from '../../../../Heading/index'
import Link from '../../../../Link/index'
import CommunityStonePath from '../../../../Icon/icons/CommunityStonePath/index'
import ExternalLink from '../../../../Icon/icons/ExternalLink/index'

const BLOCK = 'profile-review-banner'

export interface ProfileReviewBannerProps {
  /** Determines which banner variant to show:
   *  - 'ownProfile': "Show what you're exploring -" with a CTA picked by `ctaKind`.
   *    Shown on the owner's own private profile or when they have no shared playlists.
   *  - 'otherProfile': "Is your profile private?" with a "View your Profile" CTA.
   *    Shown to a logged-in viewer looking at another member's private profile.
   */
  variant: 'ownProfile' | 'otherProfile'
  /**
   * Which CTA to render. Controls both the button label and the default
   * destination:
   *   - 'viewProfile' (default): "View your Profile" → /profile/edit
   *   - 'viewLibrary':           "View your Library" → /playlist/default
   * Use 'viewLibrary' for an owner viewing their own non-private profile with
   * zero shared playlists — the Library is the surface where they'll actually
   * add content. An explicit `buttonTo` still overrides the default per-kind
   * destination.
   */
  ctaKind?: 'viewProfile' | 'viewLibrary'
  /**
   * Destination override for the CTA. When omitted the destination defaults
   * from `ctaKind` (see above). Parents pass this in the `otherProfile`
   * HiddenProfileView case so the "View your Profile" CTA lands on the viewer's
   * own profile URL (`/profile/{loggedInUuid}`) instead of the settings form.
   */
  buttonTo?: string
  staticText: InternationalStrings
}

interface ProfileReviewBannerContentProps {
  variant: 'ownProfile' | 'otherProfile'
  ctaKind: 'viewProfile' | 'viewLibrary'
  buttonTo?: string
}

function ProfileReviewBannerContent ({
  variant,
  ctaKind,
  buttonTo,
}: ProfileReviewBannerContentProps) {
  const isOwnProfile = variant === 'ownProfile'
  const isLibraryCta = ctaKind === 'viewLibrary'

  const titleOwn = useTranslatedText({ keys: ['titleOwnProfile'] })
  const titleOther = useTranslatedText({ keys: ['titleOtherProfile'] })
  const bodyOwn = useTranslatedText({ keys: ['bodyOwnProfile'] })
  const bodyOther = useTranslatedText({ keys: ['bodyOtherProfile'] })
  const viewLibraryButton = useTranslatedText({ keys: ['viewLibraryButton'] })
  const viewProfileButton = useTranslatedText({ keys: ['viewProfileButton'] })

  const title = isOwnProfile ? titleOwn : titleOther
  const body = isOwnProfile ? bodyOwn : bodyOther
  const buttonLabel = isLibraryCta ? viewLibraryButton : viewProfileButton

  const resolvedButtonTo = buttonTo
    ?? (isLibraryCta ? '/playlist/default' : '/profile/edit')

  const modifier = isOwnProfile ? 'own-profile' : 'other-profile'

  return (
    <div className={`${BLOCK} ${BLOCK}--${modifier}`}>
      <CommunityStonePath
        size="78px"
        color="#FFFFFF"
        className={`${BLOCK}__icon`}
      />
      <div className={`${BLOCK}__text`}>
        <H4 className={`${BLOCK}__title`}>{title}</H4>
        <p className={`${BLOCK}__body`}>{body}</p>
      </div>
      <Link
        to={resolvedButtonTo}
        className={`${BLOCK}__button`}
      >
        <span className={`${BLOCK}__button-label`}>{buttonLabel}</span>
        <ExternalLink
          size="16px"
          color="#FFFFFF"
          className={`${BLOCK}__button-icon`}
        />
      </Link>
    </div>
  )
}

function ProfileReviewBanner (props: ProfileReviewBannerProps) {
  const {
    variant, staticText, ctaKind = 'viewProfile', buttonTo,
  } = props

  return (
    <StaticTextProvider staticText={staticText}>
      <ProfileReviewBannerContent
        variant={variant}
        ctaKind={ctaKind}
        buttonTo={buttonTo}
      />
    </StaticTextProvider>
  )
}

const MemoizedProfileReviewBanner = memo(ProfileReviewBanner)

export default connectStaticText({ storeKey: 'profileReviewBanner' })(MemoizedProfileReviewBanner)
